import { useState } from 'react'
import { Field, Form } from 'react-final-form'
import { Element } from '@phrasee/phrasee-typings'
import { ValidationErrors } from 'final-form'

import Button from 'common/components/button'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import Modal from 'common/components/Modal'

import { useContent } from '../hooks'

const MAX_ELEMENT_NAME_LENGTH = 36

type Props = {
  element: Element
  onClose: () => void
  onConfirm: (newName: string) => void
}

const DuplicateElementModal = ({ element, onClose, onConfirm }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const { content } = useContent()

  const elementName = element?.display_name || element?.name || ''

  // Find the next available copy number
  const getNextCopyName = () => {
    const allElements = content?.elements || []
    const existingNames = allElements.map(
      (el) => el.display_name || el.name || ''
    )

    let copyNumber = 1
    let proposedName = `Copy ${copyNumber} of ${elementName}`

    // Keep incrementing until we find a unique name
    while (existingNames.includes(proposedName)) {
      copyNumber++
      proposedName = `Copy ${copyNumber} of ${elementName}`
    }

    return proposedName
  }

  const defaultCopyName = getNextCopyName()

  const handleSubmit = async (values: { element_name: string }) => {
    setIsLoading(true)
    try {
      await onConfirm(values.element_name)
      onClose()
    } catch (error) {
      return { element_name: 'Failed to duplicate element' }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      visible={true}
      centered
      closable={true}
      onClose={onClose}
      footer={null}
      data-cy="duplicate-element-modal"
      data-testid="duplicate-element-modal"
      style={{ minWidth: 500 }}
    >
      <div className="flex w-109">
        <h1 className="mt-2 text-2xl font-bold text-coolGray-800">
          Duplicate element
        </h1>
      </div>

      <div className="mt-4 text-base text-coolGray-600">
        <p className="mb-6">
          Only existing settings and language variants will be duplicated.
        </p>

        <Form
          onSubmit={handleSubmit}
          initialValues={{
            element_name: defaultCopyName,
          }}
          validate={(values) => {
            const errors: ValidationErrors = {}
            if (!values.element_name?.trim()) {
              errors.element_name = 'Name is required'
            }
            if (values.element_name?.length > MAX_ELEMENT_NAME_LENGTH) {
              errors.element_name = `Name should be up to ${MAX_ELEMENT_NAME_LENGTH} characters`
            }
            return errors
          }}
          render={({ handleSubmit, hasValidationErrors, pristine, values }) => (
            <form onSubmit={handleSubmit} className="text-sm">
              <Field<string>
                name="element_name"
                render={({ input, meta }) => (
                  <FormItem
                    label="Name"
                    htmlFor="element_name"
                    error={
                      meta.error ||
                      (!meta.dirtySinceLastSubmit && meta.submitError) ||
                      undefined
                    }
                  >
                    <div className="relative">
                      <div className="absolute right-0 -top-6 text-coolGray-500 text-xs font-normal">
                        {input.value?.length || 0} / {MAX_ELEMENT_NAME_LENGTH}
                      </div>
                      <Input
                        type="text"
                        id="element_name"
                        variant="default"
                        className="text-coolGray-800"
                        value={input.value}
                        name={input.name}
                        onChange={input.onChange}
                        disabled={isLoading}
                        maxLength={MAX_ELEMENT_NAME_LENGTH}
                      />
                    </div>
                  </FormItem>
                )}
              />
              <div className="flex justify-end mt-6">
                <Button
                  variant="link"
                  className="mr-4 text-base text-coolGray-400"
                  onClick={onClose}
                  data-cy="duplicate-element-modal-cancel-button"
                  data-testid="duplicate-element-modal-cancel-button"
                  disabled={isLoading}
                >
                  CANCEL
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={
                    isLoading || hasValidationErrors || !values.element_name
                  }
                  data-cy="duplicate-element-modal-confirm-button"
                  data-testid="duplicate-element-modal-confirm-button"
                >
                  DUPLICATE
                </Button>
              </div>
            </form>
          )}
        />
      </div>
    </Modal>
  )
}

export default DuplicateElementModal
